import { LINE_ITEM_TYPE, LineItemType } from '@/constants/ApiConstants';
import ItemSummary from '@/models/entities/items/item-summary';
import LineItem from '@/store/modules/cart/line-item';
import Cart from '@/store/modules/cart/cart';
import {
  CartControlsConfiguration,
  CartRequestVuexGetter,
  CustomerModeScanStateVuexGetter,
  GiftCardLineItemsVuexGetter,
  LineItemsVuexGetter,
  NonEmptyCartVuexGetter,
  OrderTypePropGetter,
} from '@/modules/cart/composition-configuration';
import { Variation } from '@/store/modules/cart/variation';

type CartItemControlsConfiguration = Pick<
  CartControlsConfiguration,
  | 'cartRequestVuexGetter'
  | 'nonEmptyCartVuexGetter'
  | 'lineItemsVuexGetter'
  | 'giftCardLineItemsVuexGetter'
  | 'customerModeScanStateVuexGetter'
  | 'orderTypePropGetter'
>;

export default class CartItemControls {
  private readonly getCart: CartRequestVuexGetter;

  private readonly getNonEmptyCarts: NonEmptyCartVuexGetter;

  private readonly getLineItems: LineItemsVuexGetter;

  private readonly getGiftCardItems: GiftCardLineItemsVuexGetter;

  private readonly isCustomerModeScan: CustomerModeScanStateVuexGetter;

  private readonly getOrderType: OrderTypePropGetter;

  constructor(configuration: CartItemControlsConfiguration) {
    this.getCart = configuration.cartRequestVuexGetter;
    this.getNonEmptyCarts = configuration.nonEmptyCartVuexGetter;
    this.getLineItems = configuration.lineItemsVuexGetter;
    this.getGiftCardItems = configuration.giftCardLineItemsVuexGetter;
    this.isCustomerModeScan = configuration.customerModeScanStateVuexGetter;
    this.getOrderType = configuration.orderTypePropGetter;
  }

  getCartLineItems() {
    const orderType = this.getOrderType();
    const nonEmptyCarts = this.getNonEmptyCarts();
    if (orderType && nonEmptyCarts && !this.isCustomerModeScan()) {
      const orderTypeCart = nonEmptyCarts.find(cart => cart.orderType === orderType.id);
      return orderTypeCart?.lineItems ? orderTypeCart.lineItems : [];
    }

    return this.getLineItems();
  }

  getLineItemsOfType(type: LineItemType) {
    return this.getCartLineItems().filter(lineItem => lineItem.type === type);
  }

  getItemLineItem(item: ItemSummary, variation: Variation = null) {
    if (!item) {
      return null;
    }

    return this.getLineItemsOfType(LINE_ITEM_TYPE.INVENTORY_ITEM).find(lineItem => {
      return (
        lineItem.item.id === item.id &&
        ((!lineItem.variation && !variation) || lineItem.variation === variation)
      );
    });
  }

  getLineItemCount(cart?: Cart) {
    let lineItems: Array<LineItem>;

    if (cart?.lineItems) {
      lineItems = cart.lineItems.filter(lineItem => lineItem.type === 1 && lineItem.quantity >= 0);
    } else {
      lineItems = this.getLineItemsOfType(LINE_ITEM_TYPE.INVENTORY_ITEM).filter(
        lineItem => lineItem.quantity >= 0,
      );
    }

    return lineItems.length;
  }

  getSpecialOrderItemLineItemCount(cart?: Cart) {
    let lineItems: Array<LineItem>;
    if (cart?.lineItems) {
      lineItems = cart.lineItems.filter(
        lineItem => lineItem.type === LINE_ITEM_TYPE.SPECIAL_ORDER_ITEM && lineItem.quantity >= 0,
      );
    } else {
      lineItems = this.getLineItemsOfType(LINE_ITEM_TYPE.SPECIAL_ORDER_ITEM).filter(
        lineItem => lineItem.quantity >= 0,
      );
    }

    return lineItems.length;
  }

  isEmpty() {
    const cart = this.getCart();
    return (
      (!cart?.items?.length || this.getLineItemCount() === 0) &&
      this.getSpecialOrderItemLineItemCount() === 0
    );
  }

  getCartLineItemCount(orderTypeCart: Cart) {
    if (orderTypeCart) {
      return orderTypeCart.giftCardItems ||
        orderTypeCart.specialOrderItems ||
        orderTypeCart.lineItems
        ? this.getLineItemCount(orderTypeCart) +
            orderTypeCart.giftCardItems.length +
            this.getSpecialOrderItemLineItemCount(orderTypeCart)
        : 0;
    }
    const cart = this.getCart();
    return cart && (cart.giftCardItems || cart.specialOrderItems || cart.lineItems)
      ? this.getLineItemCount() +
          this.getGiftCardItems().length +
          this.getSpecialOrderItemLineItemCount()
      : 0;
  }
}
